import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from './ThemeProvider'

interface MoodFollowUpProps {
  mood: string
  onTalkToDopa: () => void
  onUseTools: () => void
  onJustLeave: () => void
}

const MoodFollowUp = ({ mood, onTalkToDopa, onUseTools, onJustLeave }: MoodFollowUpProps) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  
  const getBotMessage = (mood: string) => {
    switch (mood.toLowerCase()) {
      case 'good':
        return "I'm glad you're feeling good today. That's wonderful to hear. Sometimes it's nice to check in even when things are going well."
      case 'calm':
        return "Feeling calm is such a gift. You've found some peace today, and that's beautiful."
      case 'okay':
        return "Okay is perfectly valid. You don't have to feel amazing all the time - just being okay is enough."
      case 'low':
        return "I hear you. Feeling low can be really hard. You're not alone in this, and it's brave of you to notice how you're feeling."
      case 'anxious':
        return "Anxiety can feel overwhelming. You're safe here, and taking a moment to acknowledge these feelings is already a step toward caring for yourself."
      case 'tired':
        return "Being tired - whether physically, emotionally, or both - is so valid. Rest isn't just okay, it's necessary."
      case 'unsure':
        return "Not knowing how you feel is completely normal. Sometimes our emotions are complex or shifting, and that's okay too."
      case 'frustrated':
        return "Frustration can feel like pressure building up inside. Those feelings are real and important - you don't have to carry them alone."
      default:
        return "Thank you for sharing how you're feeling with me. Whatever you're experiencing right now is valid and important."
    }
  }

  const getActionSuggestion = (mood: string) => {
    switch (mood.toLowerCase()) {
      case 'good':
      case 'calm':
        return "Would you like to talk about what's contributing to these good feelings, or explore some gentle tools to maintain this sense of wellbeing?"
      case 'okay':
        return "Would you like to talk about your day, or try some gentle activities that might add a little more lightness?"
      case 'low':
      case 'anxious':
      case 'frustrated':
        return "Would you like to talk through what you're experiencing, or try some calming tools that might help ease these feelings?"
      case 'tired':
        return "Would you like to talk about what's been draining your energy, or explore some gentle ways to find rest and restoration?"
      case 'unsure':
        return "Would you like to talk through your feelings to help sort them out, or try some mindful activities that might bring clarity?"
      default:
        return "Would you like to talk about what you're experiencing, or explore some tools that might help?"
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 sm:p-8">
      <div className="bg-neutral-25 dark:bg-sage-800 rounded-3xl p-6 sm:p-8 max-w-md w-full relative">
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-4 bg-sage-100/60 dark:bg-sage-700/60 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-sage-400 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          
          <p className="text-neutral-600 dark:text-neutral-300 font-light text-sm sm:text-base leading-relaxed mb-6">
            {getBotMessage(mood)}
          </p>
          
          <p className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm leading-relaxed mb-8">
            {getActionSuggestion(mood)}
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={onTalkToDopa}
            className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-peach-50/60 hover:bg-peach-100/50 
                       dark:bg-peach-900/30 dark:hover:bg-peach-800/40
                       text-peach-600 dark:text-peach-300 rounded-2xl text-sm sm:text-base font-light 
                       border border-peach-100/40 dark:border-peach-700/30 
                       hover:border-peach-200/50 dark:hover:border-peach-600/40
                       transition-colors duration-300 focus:outline-none focus:ring-4 
                       focus:ring-peach-100/50 dark:focus:ring-peach-700/30
                       flex items-center justify-center space-x-3"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            <span>Talk to Dopa</span>
          </button>
          
          <button
            onClick={onUseTools}
            className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-sage-50/60 hover:bg-sage-100/50 
                       dark:bg-sage-800/60 dark:hover:bg-sage-700/60
                       text-sage-600 dark:text-sage-300 rounded-2xl text-sm sm:text-base font-light 
                       border border-sage-100/40 dark:border-sage-700/40 
                       hover:border-sage-200/50 dark:hover:border-sage-600/50
                       transition-colors duration-300 focus:outline-none focus:ring-4 
                       focus:ring-sage-100/50 dark:focus:ring-sage-700/30
                       flex items-center justify-center space-x-3"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span>Try gentle tools</span>
          </button>
          
          <button
            onClick={onJustLeave}
            className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-neutral-50/60 hover:bg-neutral-100/50 
                       dark:bg-neutral-800/40 dark:hover:bg-neutral-700/50
                       text-neutral-500 dark:text-neutral-400 rounded-2xl text-xs sm:text-sm font-light 
                       border border-neutral-100/40 dark:border-neutral-700/30 
                       hover:border-neutral-200/50 dark:hover:border-neutral-600/40
                       transition-colors duration-300 focus:outline-none focus:ring-4 
                       focus:ring-neutral-100/50 dark:focus:ring-neutral-700/30"
          >
            That's all for now
          </button>
        </div>
      </div>
    </div>
  )
}

const MoodCheckIn = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [selectedMood, setSelectedMood] = useState<string | null>(null)
  const [showFollowUp, setShowFollowUp] = useState(false)
  const [showThankYou, setShowThankYou] = useState(false)

  const moods = [
    { emoji: '😊', label: 'Good', color: 'mint' },
    { emoji: '😌', label: 'Calm', color: 'sage' },
    { emoji: '😐', label: 'Okay', color: 'cream' },
    { emoji: '😔', label: 'Low', color: 'peach' },
    { emoji: '😰', label: 'Anxious', color: 'peach' },
    { emoji: '😴', label: 'Tired', color: 'sage' },
    { emoji: '🤔', label: 'Unsure', color: 'cream' },
    { emoji: '😤', label: 'Frustrated', color: 'peach' }
  ]

  const saveMoodContext = (mood: string) => {
    // Save mood to localStorage for bot context
    const moodEntry = {
      mood,
      timestamp: new Date().toISOString(),
      date: new Date().toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    }
    
    localStorage.setItem('current_mood_context', JSON.stringify(moodEntry))
    console.log('Mood context saved:', moodEntry)
  }

  const handleMoodSelect = (mood: string) => {
    setSelectedMood(mood)
    saveMoodContext(mood)
    setShowFollowUp(true)
  }

  const handleTalkToDopa = () => {
    // Navigate to support with mood context
    navigate('/support')
  }

  const handleUseTools = () => {
    setShowFollowUp(false)
    setShowThankYou(true)
    setTimeout(() => {
      navigate('/tools')
    }, 2000)
  }

  const handleJustLeave = () => {
    setShowFollowUp(false)
    setShowThankYou(true)
    setTimeout(() => {
      navigate('/tools')
    }, 2000)
  }

  if (showThankYou) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12 bg-neutral-25 dark:bg-sage-900">
        <div className="text-center">
          <div className="text-2xl sm:text-3xl mb-6">💙</div>
          <h2 className="text-neutral-600 dark:text-neutral-300 font-light text-lg sm:text-xl mb-4">
            Thank you for checking in
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 font-light text-sm sm:text-base leading-relaxed max-w-xs mx-auto mb-4">
            You chose: <span className="font-medium">{selectedMood}</span>
          </p>
          <p className="text-neutral-400 dark:text-neutral-500 font-light text-xs sm:text-sm leading-relaxed max-w-xs mx-auto">
            However you're feeling is okay. You're doing great by noticing.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-neutral-25 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-neutral-50/80 dark:bg-sage-800/80 border-b border-neutral-100/50 dark:border-sage-700/50">
        <button
          onClick={() => navigate('/tools')}
          className="p-3 sm:p-4 rounded-2xl hover:bg-neutral-100 dark:hover:bg-sage-700 transition-colors focus:outline-none focus:ring-4 focus:ring-neutral-100 dark:focus:ring-sage-700"
        >
          <svg className="w-5 h-5 text-neutral-500 dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-base sm:text-lg font-light text-neutral-600 dark:text-neutral-300">Mood Check-In</h1>
        <div className="w-11 sm:w-12"></div>
      </div>

      {/* Check-In */}
      <div className="flex-1 flex flex-col px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <div className="text-xl sm:text-2xl mb-4">🌈</div>
          <p className="text-neutral-600 dark:text-neutral-300 font-light text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
            How are you feeling — even if you're not sure?
          </p>
        </div>

        {/* Mood Options */}
        <div className="max-w-sm mx-auto w-full mb-8 sm:mb-12">
          <h3 className="text-neutral-500 dark:text-neutral-400 font-light text-xs sm:text-sm mb-6 text-center">
            Pick what feels closest:
          </h3>
          
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-12">
            {moods.map((mood, index) => (
              <button
                key={index}
                onClick={() => handleMoodSelect(mood.label)}
                className="p-4 sm:p-6 bg-neutral-50 dark:bg-sage-800 hover:bg-neutral-100/80 dark:hover:bg-sage-700/80 
                           text-neutral-600 dark:text-neutral-300 rounded-2xl border border-neutral-100/50 dark:border-sage-700/50 hover:border-neutral-200/60 dark:hover:border-sage-600/60
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-neutral-100 dark:focus:ring-sage-700
                           flex flex-col items-center space-y-2"
              >
                <span className="text-xl sm:text-2xl">{mood.emoji}</span>
                <span className="text-xs sm:text-sm font-light">{mood.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="text-center space-y-4">
          <button
            onClick={() => navigate('/tools')}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-sage-100/60 hover:bg-sage-200/50 
                       dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                       text-sage-600 dark:text-sage-300 rounded-2xl text-sm sm:text-base font-light 
                       border border-sage-200/40 dark:border-sage-600/40 
                       hover:border-sage-300/50 dark:hover:border-sage-500/50
                       transition-colors duration-300 focus:outline-none focus:ring-4 
                       focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
          >
            Return to Tools
          </button>
          
          <button
            onClick={() => handleMoodSelect('Unsure')}
            className="block mx-auto text-neutral-400 dark:text-neutral-500 text-xs sm:text-sm hover:text-neutral-500 dark:hover:text-neutral-400 transition-colors"
          >
            I don't know
          </button>
        </div>

        {/* Gentle Footer */}
        <div className="text-center mt-12 sm:mt-16">
          <p className="text-neutral-400 dark:text-neutral-500 text-xs sm:text-sm font-light leading-relaxed max-w-xs mx-auto">
            There's no right answer. Just pick what feels closest to you right now.
          </p>
        </div>
      </div>

      {/* Follow-up Modal */}
      {showFollowUp && selectedMood && (
        <MoodFollowUp
          mood={selectedMood}
          onTalkToDopa={handleTalkToDopa}
          onUseTools={handleUseTools}
          onJustLeave={handleJustLeave}
        />
      )}
    </div>
  )
}

export default MoodCheckIn